<script setup>
import { useMutation } from '@vue/apollo-composable';
import { useVuelidate } from '@vuelidate/core';
import { required, email, maxLength, minValue, sameAs } from '@vuelidate/validators';

const loading = ref(false);
const success = ref(false);

const { t } = useI18n();
const route = useRoute();
const gtm = useGtm();

const state = reactive({
  attendees_count: '',
  atendee_name: '',
  age: '',
  atendee_legal_representative_name: '',
  email: '',
  phone_number: '',
  agreement: '',
  comment: '',
});

const rules = {
  attendees_count: {
    required,
    minValueValue: minValue(1),
  },
  atendee_name: { required },
  age: { required },
  atendee_legal_representative_name: { required },
  email: { required, email },
  phone_number: { required },
  agreement: { sameAs: sameAs(true) },
  comment: { maxLength: maxLength(255) },
};

const v$ = ref(useVuelidate(rules, state));

const query = gql`
  mutation ($event_id: Int!, $data: String!) {
    createEventReservation(event_id: $event_id, data: $data)
  }
`;

const { mutate: createEventReservation } = useMutation(query, { event_id: route.params.id, data: JSON.stringify(state) });

const submitForm = async () => {
  loading.value = true;
  try {
    const isFormCorrect = await v$.value.$validate();

    if (!isFormCorrect) {
      loading.value = false;
      return;
    }
    loading.value = false;
    success.value = true;
    createEventReservation({ event_id: parseInt(route.params.id), data: JSON.stringify(state) });
    gtm.trackEvent({ event: 'eventDetail_reservationSuccess', eventId: route.params.id, url: window.location.href.replace(window.location.origin, '') });
  } catch (error) {
    loading.value = false;
    gtm.trackEvent({ event: 'eventDetail_reservationErr', eventId: route.params.id, err: error, url: window.location.href.replace(window.location.origin, '') });
  }
};
</script>

<template>
  <div v-if="!success">
    <form class="form mt-5" @submit.prevent="validate">
      <div class="mt-4 b-flex">
        <div class="b-flex-child">
          <div class="form-input">
            <label :for="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-1'))}`" class="form-input__label">{{
              t('components.reservations.form-programs-ateliers-children-1')
            }}</label>
            <div class="form-input__elem">
              <input
                :id="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-1'))}`"
                v-model.trim="state.attendees_count"
                :type="'text'"
                class="input input--full input--box"
                :aria-label="t('components.reservations.form-programs-ateliers-children-1')"
              />
            </div>
          </div>
          <p class="form--error-message" v-if="v$.attendees_count?.$errors.length">
            {{ t('components.reservations.form-programs-ateliers-children-2') }}
          </p>
        </div>
        <div lg="6 b-flex-child" class="mt-lg-0 b-flex-child">
          <div class="form-input">
            <label :for="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-4'))}`" class="form-input__label">{{
              t('components.reservations.form-programs-ateliers-children-4')
            }}</label>
            <div class="form-input__elem">
              <input
                :id="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-4'))}`"
                v-model.trim="state.atendee_name"
                :type="'text'"
                class="input input--full input--box"
                :aria-label="t('components.reservations.form-programs-ateliers-children-4')"
              />
            </div>
          </div>
          <p class="form--error-message" v-if="v$.atendee_name?.$errors.length">
            {{ t('components.reservations.form-programs-ateliers-children-5') }}
          </p>
        </div>
      </div>

      <div class="mt-4 b-flex">
        <div class="b-flex-child">
          <div class="form-input">
            <label :for="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-6'))}`" class="form-input__label">{{
              t('components.reservations.form-programs-ateliers-children-6')
            }}</label>
            <div class="form-input__elem">
              <input
                :id="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-6'))}`"
                v-model.trim="state.attendees_count"
                :type="'text'"
                class="input input--full input--box"
                :aria-label="t('components.reservations.form-programs-ateliers-children-6')"
              />
            </div>
          </div>
          <p class="form--error-message" v-if="v$.attendees_count?.$errors.length">
            {{ t('components.reservations.form-programs-ateliers-children-7') }}
          </p>
        </div>
        <div lg="6 b-flex-child" class="mt-lg-0 b-flex-child">
          <div class="form-input">
            <label :for="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-8'))}`" class="form-input__label">{{
              t('components.reservations.form-programs-ateliers-children-8')
            }}</label>
            <div class="form-input__elem">
              <input
                :id="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-8'))}`"
                v-model.trim="state.school_name"
                :type="'text'"
                class="input input--full input--box"
                :aria-label="t('components.reservations.form-programs-ateliers-children-8')"
              />
            </div>
          </div>
          <p class="form--error-message" v-if="v$.school_name?.$errors.length">
            {{ t('components.reservations.form-programs-ateliers-children-9') }}
          </p>
        </div>
      </div>

      <div class="mt-4 b-flex">
        <div class="b-flex-child">
          <div class="form-input">
            <label :for="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-10'))}`" class="form-input__label">{{
              t('components.reservations.form-programs-ateliers-children-10')
            }}</label>
            <div class="form-input__elem">
              <input
                :id="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-10'))}`"
                v-model.trim="state.attendees_count"
                :type="'text'"
                class="input input--full input--box"
                :aria-label="t('components.reservations.form-programs-ateliers-children-10')"
              />
            </div>
          </div>
          <p class="form--error-message" v-if="v$.attendees_count?.$errors.length">
            {{ t('components.reservations.form-programs-ateliers-children-11') }}
          </p>
        </div>
        <div lg="6 b-flex-child" class="mt-lg-0 b-flex-child">
          <div class="form-input">
            <label :for="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-13'))}`" class="form-input__label">{{
              t('components.reservations.form-programs-ateliers-children-13')
            }}</label>
            <div class="form-input__elem">
              <input
                :id="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-13'))}`"
                v-model.trim="state.phone_number"
                :type="'text'"
                class="input input--full input--box"
                :aria-label="t('components.reservations.form-programs-ateliers-children-13')"
              />
            </div>
          </div>
          <p class="form--error-message" v-if="v$.phone_number?.$errors.length">
            {{ t('components.reservations.form-programs-ateliers-children-14') }}
          </p>
        </div>
      </div>

      <div class="mt-4 b-flex">
        <div class="b-flex-child">
          <div class="form-input">
            <label :for="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-15'))}`" class="form-input__label">{{
              t('components.reservations.form-programs-ateliers-children-15')
            }}</label>
            <div class="form-input__elem">
              <input
                :id="`input_${useSlugify(t('components.reservations.form-programs-ateliers-children-15'))}`"
                v-model.trim="state.comment"
                :type="'text'"
                class="input input--full input--box"
                :aria-label="t('components.reservations.form-programs-ateliers-children-15')"
              />
            </div>
          </div>
          <p class="form--error-message" v-if="v$.comment?.$errors.length">
            {{ t('components.reservations.form-programs-ateliers-children-16') }}
          </p>
        </div>
      </div>

      <div class="mt-4 b-flex">
        <b-col>
          <b-form-checkbox v-model="$v.formData.agreement.$model" class="checkbox" :value="true">
            <i18n path="pages.newsletter.agreement.text" tag="span">
              <a
                :href="
                  localePath({
                    name: 'about-page',
                    params: {
                      slug: 'gdpr',
                    },
                  })
                "
                class="link"
                target="_blank"
                >{{ t('pages.newsletter.agreement.link') }}</a
              >
            </i18n>
          </b-form-checkbox>
          <b-form-invalid-feedback v-if="!$v.formData.agreement.checked && $v.formData.agreement.$error">
            {{ t('pages.newsletter.validation.terms-agreement') }}
          </b-form-invalid-feedback>
        </b-col>
      </div>

      <div class="mt-4">
        <button class="button" :disabled="loading" @click="submitForm">
          {{ t('components.reservations.submit') }}
        </button>
      </div>

      <div class="mt-6 editor-paragraph">
        <p v-html="t('global.forms.agreement')" />
      </div>
    </form>
  </div>
  <div v-else>
    <h2 class="heading heading--2 mt-6">{{ t('pages.event.thx-after-buy') }}</h2>
  </div>
</template>

<style lang="scss" scoped>
.b-flex-child {
  width: 50%;
}

.form--error-message {
  color: #ef5858;
  margin-top: 12px;
  font-size: 15px;
  //letter-spacing: 0.2px;
}
</style>
